import { mergeSxProps, Slider } from "@clipboard-health/ui-components";
import { type SxProps, type Theme } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { getMarks } from "./utils";

interface PaySliderProps {
  value?: number;
  range: {
    minimumRate: number;
    maximumRate: number;
  };
  averageWinningBid: number | undefined;
  onChange: (value?: number) => void;
  disabled?: boolean;
  sx?: SxProps;
  railBackgroundColor?: (theme: Theme) => string | undefined;
}

const STEP = 0.25;

export function PaySlider(props: PaySliderProps) {
  const { range, value, averageWinningBid, disabled, sx, onChange, railBackgroundColor } = props;

  const { minimumRate, maximumRate } = range;

  const marks = getMarks({ minimumRate, maximumRate, averageWinningBid });

  return (
    <Slider
      thumb
      disabled={disabled}
      marks={marks}
      variant="circular"
      color="primary"
      min={minimumRate}
      max={maximumRate}
      step={STEP}
      value={value}
      valueLabelFormat={(value) => `${formatDollarsAsUsd(value)}`}
      // Uses same slider configuration as DistanceSlider. These should be unified later.
      sx={mergeSxProps(
        (theme) => ({
          width: "100%",
          height: "0.5rem",

          // DistanceSlider uses padding: "1rem",
          // PaySlider needed to override it to only pad the bottom (it was being shifted to the right otherwise)
          paddingBottom: "3rem",

          // compensate for mark labels which uses position absolute
          // and is below the slider
          marginBottom: "3rem",

          ".MuiSlider-markLabel": {
            marginTop: "0.6rem",
          },

          ...(averageWinningBid
            ? {
                "& .MuiSlider-markLabel[data-index='1']": {
                  marginTop: "-1.25rem",
                  fontSize: "1.5rem",
                  color: theme.palette.action.active,
                },
              }
            : {}),

          "& .MuiSlider-rail": {
            height: "0.5rem",
            color: railBackgroundColor?.(theme) ?? theme.palette.background?.primary,
          },

          "& .MuiSlider-thumb": {
            display: "flex",
            // color: theme.palette.border?.main,
            backgroundColor: theme.palette.background.tertiary,
            border: `1px solid ${theme.palette.primary?.main}`,
            height: "1.5rem",
            width: "1.5rem",

            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='8.5' height='10'>
                     <rect x='0' y='1' width='1' height='8' fill='${theme.palette.primary?.main}'/>
                     <rect x='3.5' y='0' width='1' height='12' fill='${theme.palette.primary?.main}'/>
                     <rect x='7' y='1' width='1' height='8' fill='${theme.palette.primary?.main}'/>
                   </svg>`
            )}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",

            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0px 0px 0px 1px ${theme.palette.primary?.main}`,
            },
            "&.Mui-active": {
              boxShadow: `0px 0px 0px 2px ${theme.palette.primary?.main}`,
            },
          },
        }),
        sx
      )}
      onChange={(_, value) => {
        onChange(value as number);
      }}
    />
  );
}
